import { type ReactElement } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
} from '@mui/material';

export interface SectionVisibilityDialogProps {
  open: boolean;
  sectionName: string;
  /** Current visibility of the section; the confirmed action flips it. */
  isVisible: boolean;
  isUpdating: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function SectionVisibilityDialog({
  open,
  sectionName,
  isVisible,
  isUpdating,
  onClose,
  onConfirm,
}: SectionVisibilityDialogProps): ReactElement {
  const hiding = isVisible;
  const title = hiding ? 'Hide section' : 'Show section';
  const confirmLabel = hiding ? 'Yes, hide' : 'Yes, show';
  const consequence = hiding
    ? `Hiding the “${sectionName}” section makes it disappear from the website preview straight away. To make this change permanent on the live website, you need to publish it.`
    : `Showing the “${sectionName}” section makes it appear on the website preview straight away. To make this change permanent on the live website, you need to publish it.`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle className="vtwo:flex vtwo:items-center vtwo:justify-between vtwo:font-bold">
        {title}
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <DialogContentText className="vtwo:text-typography-primary">
          {consequence}
        </DialogContentText>
      </DialogContent>
      <DialogActions className="vtwo:px-6 vtwo:pb-6 vtwo:gap-3">
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isUpdating}
          className="vtwo:flex-1 vtwo:text-typography-primary vtwo:border-plus-border"
          sx={{ borderRadius: '8px' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={isUpdating}
          className="vtwo:flex-1"
          sx={{ borderRadius: '8px' }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
